import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, TrendingUp, ArrowRight } from "lucide-react";
import { AssessmentQuestion } from "./types";
import { AnswerData } from "./answerTypes";
import { useNavigate } from "react-router-dom";
import { identifyKeyStrengths, getStrengthMessage, getFallbackMessage } from '@/utils/strengthsEvaluation';
interface MySuccessionCompletionScreenProps {
  question: AssessmentQuestion;
  onFinish: () => void;
  answers: AnswerData;
}
export function MySuccessionCompletionScreen({
  question,
  onFinish,
  answers
}: MySuccessionCompletionScreenProps) {
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const keyStrengths = identifyKeyStrengths(answers);
  const fallbackMessage = getFallbackMessage(keyStrengths.length);
  useEffect(() => {
    // Simulate content generation process
    const timer = setTimeout(() => {
      setIsGenerating(false);
      setShowContent(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);
  return <div className="text-center space-y-8 py-8 w-full max-w-4xl mx-auto min-h-[500px] flex flex-col justify-center">
      <div className="space-y-6">
        <div className="space-y-4">
          <h1 className="font-bold text-foreground text-xl">
            Thanks for sharing where you are today — we've got a great starting point
          </h1>
          <h2 className="text-lg text-muted-foreground max-w-2xl mx-auto">
            We're now developing a custom guide for your Next Steps based on your answers and have identified key areas of opportunity we can build together.
          </h2>
        </div>

        {isGenerating && <div className="space-y-4">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
            <p className="text-muted-foreground">Analyzing your responses...</p>
            <Progress value={66} className="w-64 mx-auto" />
          </div>}

        {showContent && <div className="space-y-6">
            {keyStrengths.length > 0 ? <div className="bg-card border rounded-lg p-6 space-y-4">
                
                <div className="space-y-4">
                  {keyStrengths.map((strength, index) => <div key={index} className="bg-muted/30 rounded-lg p-4 text-left space-y-2">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-success mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-foreground">
                          <span className="font-semibold">{strength}:</span> {getStrengthMessage(strength)}
                        </p>
                      </div>
                    </div>)}
                </div>
                
                {fallbackMessage && <div className="border-t pt-4">
                    <div className="bg-muted/30 rounded-lg p-4 text-left space-y-2">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-warning mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-foreground">
                          <span className="font-semibold">What's Next:</span> {fallbackMessage}
                        </p>
                      </div>
                    </div>
                  </div>}
              </div> : <div className="bg-card border rounded-lg p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-6 w-6 text-primary" />
                  <h3 className="text-xl font-semibold">Assessment Complete</h3>
                </div>
                <p className="text-muted-foreground text-left">
                  {fallbackMessage}
                </p>
              </div>}

          </div>}
      </div>
      
      {showContent && <div className="flex gap-4 justify-center">
          <Button size="sm" onClick={onFinish} variant="outline" className="px-4 py-2 h-auto text-sm">
            Next Assessment
          </Button>
          <Button size="sm" onClick={() => navigate("/dashboard")} className="bg-accent hover:bg-accent/90 text-accent-foreground px-4 py-2 h-auto text-sm">
            Go to Dashboard
          </Button>
        </div>}
    </div>;
}